/**
 * Headless authoritative simulation: player motor + level + triggers +
 * attempts. Has no knowledge of rendering, DOM or wall-clock time, which is
 * what lets the automated tests run the real game logic in Node.
 */
import { EventBus } from '../core/Events';
import { Vec3 } from '../core/Vec3';
import type { EngineConfig } from '../core/Config';
import type { InputFrame, RecordedInput } from '../input/InputFrame';
import { AttemptManager, type DeathInfo, type GameMode, type StartCheck, type GameplayEvents } from '../gameplay/AttemptManager';
import { MemoryRecordStore, type RecordStore } from '../gameplay/RecordStore';
import { MovementRuleSet } from '../movement/MovementRules';
import { PlayerMotor } from '../movement/PlayerMotor';
import { PlayerState } from '../movement/PlayerState';
import { ProximityContact } from './CollisionQueries';
import type { LoadedLevel } from '../world/LevelLoader';
import type { LevelData, SpawnPoint } from '../world/LevelData';
import { TriggerSystem, type TriggerEvent } from '../world/TriggerSystem';

export interface SimulationOptions {
  rules?: MovementRuleSet;
  records?: RecordStore;
  /** Ticks to wait after death before respawning (0 = next tick). */
  respawnDelayTicks?: number;
  /** Keep the inputs of the current attempt for replay/export. */
  recordInputs?: boolean;
}

const DEG = Math.PI / 180;
/** Minimum clearance between a spawn capsule and any lethal surface. */
export const SPAWN_LETHAL_CLEARANCE = 0.5;
/** Developer spawns (lab spawn, replays) must lie within this many metres of the origin. */
export const MAX_SPAWN_COORD = 10_000;
/** Death-to-respawn delay the game uses (0.25 s at 120 Hz). Recordings store the delay they ran with. */
export const GAME_RESPAWN_DELAY_TICKS = 30;

/** Stable, order-insensitive hash for record keys (FNV-1a over sorted JSON). */
export function configHash(value: unknown): string {
  const json = JSON.stringify(value, (_k, v: unknown) =>
    v && typeof v === 'object' && !Array.isArray(v) ? Object.fromEntries(Object.entries(v).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))) : v,
  );
  let h = 0x811c9dc5;
  for (let i = 0; i < json.length; i++) {
    h ^= json.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16).padStart(8, '0');
}

/** Level part of a record key. Compiled tracks are keyed by their gameplay fingerprint; built-in levels keep their legacy keys. */
export function recordKeyBase(level: Pick<LevelData, 'id' | 'version' | 'contentHash'>): string {
  return level.contentHash ? `${level.id}@${level.contentHash.slice(0, 16)}` : `${level.id}@v${level.version}`;
}

/** Movement part of a record key: physics configuration plus enabled rules and their parameters. */
export function movementKey(config: Readonly<EngineConfig>, rules: MovementRuleSet): string {
  return configHash({ m: config.movement, p: config.player, t: config.simulation.tickRate, r: rules.enabledIds().map((id) => [id, rules.get(id).params]) });
}

/** The record key a level has under the given configuration and rules (Simulation.recordKey without a simulation). */
export function recordKeyFor(level: Pick<LevelData, 'id' | 'version' | 'contentHash'>, config: Readonly<EngineConfig>, rules: MovementRuleSet): string {
  return `${recordKeyBase(level)}#${movementKey(config, rules)}`;
}

export class Simulation {
  readonly dt: number;
  readonly state = new PlayerState();
  private currentMotor: PlayerMotor;
  private currentConfig: Readonly<EngineConfig>;
  readonly triggers: TriggerSystem;
  readonly attempts: AttemptManager;
  readonly events = new EventBus<GameplayEvents>();
  /** Trigger events from the most recent tick (for diagnostics). */
  lastTriggerEvents: readonly TriggerEvent[] = [];
  private respawnCountdown = -1;
  private pendingSpawn: SpawnPoint | null = null;
  readonly respawnDelayTicks: number;
  private readonly spawnVec = new Vec3();
  readonly recordedInputs: RecordedInput[] = [];
  private readonly recordInputs: boolean;
  private readonly recordKeyBase: string;
  /** Sees every tick's input before it is applied (run capture). Observes only; must not touch the simulation. */
  inputObserver: ((input: Readonly<InputFrame>, tick: number) => void) | null = null;

  constructor(
    readonly level: LoadedLevel,
    config: Readonly<EngineConfig>,
    options: SimulationOptions = {},
  ) {
    this.currentConfig = config;
    this.dt = 1 / config.simulation.tickRate;
    this.currentMotor = new PlayerMotor(level.physics, config, options.rules);
    const r = this.motor.shape.radius;
    this.triggers = new TriggerSystem(level.data.triggers, r, this.motor.shape.halfSegment * 2);
    this.respawnDelayTicks = options.respawnDelayTicks ?? 0;
    this.recordInputs = options.recordInputs ?? false;
    this.recordKeyBase = recordKeyBase(level.data);
    this.attempts = new AttemptManager(level.data, this.dt, options.records ?? new MemoryRecordStore(), () => this.recordKey(), this.events);
    this.validateSpawns();
    this.motor.attach(this.state);
    this.placeAt(level.data.start, 'restart');
  }

  get config(): Readonly<EngineConfig> {
    return this.currentConfig;
  }

  get motor(): PlayerMotor {
    return this.currentMotor;
  }

  /**
   * Live tuning: swaps in a new movement configuration without touching the
   * player, attempt, checkpoint, trigger or respawn state. Only the motor
   * reads movement parameters, so only the motor is rebuilt (components that
   * precompute from the config are rebuilt with it). Player shape and tick
   * rate must not change. A running full-run attempt is invalidated because
   * its record key changes.
   */
  reconfigure(config: Readonly<EngineConfig>): void {
    if (configHash(config.player) !== configHash(this.currentConfig.player) || config.simulation.tickRate !== this.currentConfig.simulation.tickRate) {
      throw new Error('reconfigure() only accepts movement changes; player shape and tick rate are fixed for a simulation');
    }
    this.currentMotor = new PlayerMotor(this.level.physics, config, this.currentMotor.rules);
    this.currentMotor.attach(this.state);
    this.currentConfig = config;
    this.attempts.invalidate('movement configuration changed');
  }

  /** Record key: level + version + physics configuration + enabled rules and their parameters. */
  recordKey(): string {
    return `${this.recordKeyBase}#${movementKey(this.config, this.motor.rules)}`;
  }

  get mode(): GameMode {
    return this.attempts.mode;
  }

  /** Advances one fixed tick. */
  step(input: Readonly<InputFrame>): void {
    const tickIndex = this.state.tick;
    if (this.recordInputs) this.recordedInputs.push({ ...input, tick: tickIndex });
    this.inputObserver?.(input, tickIndex);

    if (this.respawnCountdown >= 0) {
      if (this.respawnCountdown === 0 && this.pendingSpawn) {
        this.placeAt(this.pendingSpawn, 'death');
        this.pendingSpawn = null;
        this.respawnCountdown = -1;
      } else {
        this.respawnCountdown--;
      }
      this.state.previousPosition.copy(this.state.position);
      this.state.tick++;
      return;
    }

    const wasAlive = this.state.alive;
    const motion = this.motor.step(this.state, input, this.dt);
    const st = this.state;
    let death: DeathInfo | null = null;
    if (motion.lethal) {
      death = { cause: 'lethal-surface', colliderId: motion.lethal.colliderId, time: motion.lethal.time };
    } else if (st.position.y < this.level.data.killPlaneY) {
      death = { cause: 'out-of-bounds', colliderId: -1, time: 1 };
    } else if (!st.alive && st.deathCause) {
      death = { cause: st.deathCause, colliderId: -1, time: 1 };
    }
    this.lastTriggerEvents = this.triggers.update(motion.path, motion.pathCount, st.up);
    const start: StartCheck = { inside: this.triggers.isInside('start'), speed: st.planarSpeed(), runSpeed: this.config.movement.runSpeed };

    if (death && wasAlive) {
      st.alive = false;
      st.deathCause = death.cause;
      st.velocity.set(0, 0, 0);
      const spawn = this.attempts.processTick(tickIndex, this.lastTriggerEvents, death, start);
      this.scheduleRespawn(spawn);
    } else {
      this.attempts.processTick(tickIndex, this.lastTriggerEvents, null, start);
    }
  }

  /** Steps through a recorded input sequence. */
  replay(inputs: readonly InputFrame[]): void {
    for (const i of inputs) this.step(i);
  }

  /** R: practice → last checkpoint, full-run → start. `toStart` forces the track start. */
  restart(toStart = false): void {
    const spawn = this.attempts.restart(toStart);
    this.cancelPendingRespawn();
    this.placeAt(spawn, 'restart');
  }

  /**
   * Developer placement at an arbitrary spawn (lab spawn tool, replays).
   * Resets the attempt to the track start first, so a full run in progress is
   * never continued from a teleport.
   */
  placeAtSpawn(spawn: SpawnPoint): void {
    this.attempts.restart(true);
    this.cancelPendingRespawn();
    this.placeAt(spawn, 'restart');
  }

  setMode(mode: GameMode): void {
    const spawn = this.attempts.setMode(mode);
    this.cancelPendingRespawn();
    this.placeAt(spawn, 'mode-change');
  }

  /** Pause or stall: a running full-run attempt is invalidated, never silently continued. */
  notifyInterruption(reason: string): void {
    this.attempts.invalidate(reason);
  }

  private scheduleRespawn(spawn: SpawnPoint | null): void {
    if (!spawn) return;
    if (this.respawnDelayTicks <= 0) {
      this.placeAt(spawn, 'death');
      return;
    }
    this.pendingSpawn = spawn;
    this.respawnCountdown = this.respawnDelayTicks - 1;
  }

  private cancelPendingRespawn(): void {
    this.pendingSpawn = null;
    this.respawnCountdown = -1;
  }

  private placeAt(spawn: SpawnPoint, reason: GameplayEvents['respawn']['reason']): void {
    const [x, y, z] = spawn.position;
    this.state.respawn(this.spawnVec.set(x, y, z), spawn.yawDeg * DEG);
    this.triggers.resetOccupancy(this.state.position, this.state.up);
    this.events.emit('respawn', { attempt: this.attempts.attempt, spawn, reason });
  }

  /** Problems with placing the player at `spawn` (empty when safe). */
  spawnProblems(spawn: SpawnPoint, name = 'spawn'): string[] {
    const problems: string[] = [];
    const [x, y, z] = spawn.position;
    if (![x, y, z, spawn.yawDeg].every(Number.isFinite)) return [`${name} is not a finite position`];
    if (y <= this.level.data.killPlaneY) problems.push(`${name} is below the kill plane`);
    if ([x, y, z].some((c) => Math.abs(c) > MAX_SPAWN_COORD)) return [...problems, `${name} is outside the level bounds`];
    const buf: ProximityContact[] = [];
    const n = this.motor.resolver.nearby(new Vec3(x, y, z), new Vec3(0, 1, 0), SPAWN_LETHAL_CLEARANCE, buf);
    for (let i = 0; i < n; i++) {
      const c = buf[i]!;
      if (c.material?.kind === 'lethal') problems.push(`${name} is within ${SPAWN_LETHAL_CLEARANCE} m of a lethal surface`);
      if (c.distance < -1e-3) problems.push(`${name} intersects solid geometry`);
    }
    return problems;
  }

  /** Rejects levels whose spawn points sit inside solids or near lethal geometry. */
  private validateSpawns(): void {
    const spawns: { name: string; spawn: SpawnPoint }[] = [{ name: 'start', spawn: this.level.data.start }];
    for (const t of this.level.data.triggers) if (t.spawn) spawns.push({ name: `checkpoint ${t.id}`, spawn: t.spawn });
    for (const w of this.level.data.waypoints ?? []) spawns.push({ name: `waypoint ${w.name}`, spawn: w.spawn });
    const problems = spawns.flatMap(({ name, spawn }) => this.spawnProblems(spawn, `${name} spawn`));
    if (problems.length) throw new Error(`Level "${this.level.data.id}" has unsafe spawns:\n  ${problems.join('\n  ')}`);
  }
}
